import { useCallback, useEffect, useState } from 'react';
import {
  addToArray,
  findNode,
  isRootNode,
  removeFromArrayByIndex,
  removeFromArrayByNode,
  transverseTree
} from './CheckBoxTreeUtils';
import { CheckBoxTreeProps, CheckBoxTreeVirtualizedProps, Node, NodeWithParent } from './types';

const EMPTY: any[] = [];

export function useCheckBoxTree(
  expandedItems: CheckBoxTreeProps['expanded'] = EMPTY,
  checkedItems: CheckBoxTreeProps['checked'] = EMPTY,
  nodes: CheckBoxTreeProps['nodes'] = EMPTY,
  onCheck?: CheckBoxTreeProps['onCheck'],
  onExpand?: CheckBoxTreeProps['onExpand'],
  checkChildren?: CheckBoxTreeVirtualizedProps['checkChildren']
) {
  const [expanded, setExpanded] = useState(expandedItems);

  useEffect(() => {
    setExpanded(expandedItems || EMPTY);
  }, [expandedItems]);

  const handleExpanded = useCallback(
    (node: Node) => {
      let toExpand: string[];

      const item = findNode(node, expanded);
      if (item.match) {
        removeFromArrayByIndex(item.index, expanded); // new instance to propagate changes
        toExpand = [...expanded];
      } else {
        toExpand = [...expanded, node.value];
      }
      setExpanded(toExpand);
      onExpand?.(toExpand);
    },
    [expanded, onExpand]
  );

  const handleChecked = useCallback(
    (node: NodeWithParent) => {
      const currentlyChecked = checkedItems.concat();
      const isChecked = findNode(node, currentlyChecked);
      const isRootNodeAndCanSelectChildren = checkChildren === 'root' && isRootNode(node.value, nodes);
      if (isChecked.match) {
        removeFromArrayByIndex(isChecked.index, currentlyChecked);
      } else {
        addToArray(node, currentlyChecked);
      }
      if (checkChildren === 'parent' || isRootNodeAndCanSelectChildren) {
        const childrenAction = isChecked.match
          ? (node: Node) => {
              removeFromArrayByNode(node, currentlyChecked);
            }
          : (node: Node) => addToArray(node, currentlyChecked);

        transverseTree((node.original_children ?? node.children) || [], currentlyChecked, childrenAction);
      }
      onCheck?.(currentlyChecked);
    },
    [checkChildren, checkedItems, nodes, onCheck]
  );

  return {
    checked: checkedItems,
    expanded,
    handleChecked,
    handleExpanded
  };
}
